import React from "react";
import { Download } from "lucide-react";
import { downloadInvoice } from "../../services/billingService";
import Skeleton from "../ui/Skeleton";

/**
 * InvoiceTable
 * - Clean, fixed-width table
 * - Deterministic row height
 * - Skeleton only for PDF generation
 * - No Zoho coupling
 */

export default function InvoiceTable({ invoices }) {
  return (
    <div className="rounded-2xl border bg-white overflow-hidden">
      <table className="w-full text-sm table-fixed">
        {/* ---------------- Header ---------------- */}
        <thead className="border-b bg-gray-50 text-gray-600">
          <tr>
            <th className="px-4 py-3 text-left w-40">Invoice #</th>
            <th className="px-4 py-3 text-left w-28">Date</th>
            <th className="px-4 py-3 text-left w-24">Plan</th>
            <th className="px-4 py-3 text-left w-28">Amount</th>
            <th className="px-4 py-3 text-left w-40">Status</th>
            <th className="px-4 py-3 text-right w-32">Action</th>
          </tr>
        </thead>

        {/* ---------------- Body ---------------- */}
        <tbody className="divide-y">
          {invoices.map((invoice) => {
            const isPdfReady = Boolean(invoice.pdfPath);
            const isPaid = invoice.status === "paid";

            return (
              <tr key={invoice._id} className="align-middle">
                {/* Invoice number */}
                <td className="px-4 py-3 font-medium truncate">
                  {invoice.invoiceNumber}
                </td>

                {/* Date */}
                <td className="px-4 py-3">
                  {new Date(invoice.invoiceDate).toLocaleDateString()}
                </td>

                {/* Plan */}
                <td className="px-4 py-3 uppercase">
                  {invoice.plan}
                </td>

                {/* Amount */}
                <td className="px-4 py-3">
                  INR {invoice.totalAmount}
                </td>

                {/* Status */}
                <td className="px-4 py-3">
                  <StatusPill status={invoice.status} />
                </td>

                {/* Action */}
                <td className="px-4 py-3 text-right">
                  {isPdfReady ? (
                    <button
                      onClick={() => downloadInvoice(invoice._id)}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 hover:underline"
                    >
                      <Download size={14} />
                      Download
                    </button>
                  ) : (
                    <div className="flex justify-end">
                      <Skeleton className="h-4 w-24" />
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 🏷️ Status Pill                                                             */
/* -------------------------------------------------------------------------- */

function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-700",
    sent: "bg-amber-100 text-amber-700",
    unpaid: "bg-amber-100 text-amber-700",
    overdue: "bg-rose-100 text-rose-700",
    cancelled: "bg-gray-100 text-gray-700",
  };

  const label = {
    paid: "PAID",
    sent: "AWAITING PAYMENT",
    unpaid: "AWAITING PAYMENT",
    overdue: "OVERDUE",
    cancelled: "CANCELLED",
  };

  return (
    <span
      className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
        map[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {label[status] || status?.toUpperCase()}
    </span>
  );
}